import type { Role } from '@servicetim/shared';
import { useDeleteTenantRole, useTenantRoles } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  DropdownMenuItem,
  DropdownMenuSeparator,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { formatDate } from '../common/format';
import { RowActions } from '../common/row-actions';
import { withProblemMessage } from '../common/server-errors';
import { RoleDialog } from './role-dialog';

type Dialog = { kind: 'create' } | { kind: 'edit'; role: Role } | null;

/** `GET /admin/v1/roles` returns the whole (small) list; no paging. */
export function RoleTemplatesTable() {
  const roles = useTenantRoles();
  const remove = useDeleteTenantRole();
  const confirm = useConfirm();
  const [dialog, setDialog] = useState<Dialog>(null);

  const onDelete = useCallback(
    (role: Role) =>
      void confirm({
        title: `Delete role ${role.name}?`,
        description:
          'Refused while permission grants, field access rules or assignments still reference it.',
        confirmLabel: 'Delete',
        destructive: true,
        onConfirm: async () => {
          await withProblemMessage(() => remove.mutateAsync(role.id));
          toast.success(`Role ${role.name} deleted`);
        },
      }),
    [confirm, remove.mutateAsync],
  );

  const columns = useMemo<ColumnDef<Role, unknown>[]>(
    () => [
      {
        accessorKey: 'key',
        header: 'Key',
        cell: ({ row }) => (
          <code className="rounded bg-surface-sunken px-1 font-mono text-xs">
            {row.original.key}
          </code>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-fg">
            {row.original.name}
            {row.original.isSystem ? <Badge variant="outline">system</Badge> : null}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-fg-muted">{formatDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <RowActions label={`Actions for ${row.original.name}`}>
            <DropdownMenuItem onSelect={() => setDialog({ kind: 'edit', role: row.original })}>
              <Pencil /> Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={row.original.isSystem}
              onSelect={() => onDelete(row.original)}
            >
              <Trash2 /> Delete
            </DropdownMenuItem>
          </RowActions>
        ),
      },
    ],
    [onDelete],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={roles.data}
        isLoading={roles.isPending}
        error={roles.error}
        onRetry={() => void roles.refetch()}
        getRowId={(r) => r.id}
        caption="Tenant-wide role templates"
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> New role template
            </Button>
          </div>
        }
        emptyTitle="No role templates yet"
        emptyDescription="Projects always get project_admin, agent and customer. Add tenant-wide roles such as approver here."
        emptyAction={
          <Button onClick={() => setDialog({ kind: 'create' })}>
            <Plus /> New role template
          </Button>
        }
      />
      {dialog ? (
        <RoleDialog
          role={dialog.kind === 'edit' ? dialog.role : null}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
